import { defineStore } from "pinia";
import TenantService from "@/services/TenantService";
import { Preferences } from "@capacitor/preferences";
import { reactive, toRefs } from "vue";
import type { TenantFeatures } from "@/types/tenant.types";
import { SchultenMedia } from "@/services/DataService";

interface TenantStoreState {
    features: TenantFeatures | null;
    // Optimistic default: training is enabled for the vast majority of tenants
    // and is the backend default, so the tab shows immediately and only hides
    // once we learn this tenant disabled it (avoids a flash of "no training").
    trainingEnabled: boolean;
    // Fail-closed default: women's health is a new opt-in feature, so an
    // absent flag (older backend) or a request that hasn't resolved yet
    // must not reveal the cycle screens.
    cycleEnabled: boolean;
    // Booking runs with or without credits. Absent flag = enabled, so a tenant
    // on an older backend keeps seeing balances exactly as before.
    creditsEnabled: boolean;
}

export const useTenantStore = defineStore('tenant', () => {
    const state = reactive<TenantStoreState>({
        features: null,
        trainingEnabled: true,
        cycleEnabled: false,
        creditsEnabled: true,
    });

    const FEATURES_CACHE_KEY = 'tenant_features_cache_v2';
    const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

    // Every session transition advances this generation. Async reads capture
    // the current value and may only apply their result while it still matches.
    let sessionGeneration = 0;

    // Keep writes/removals ordered so a Preferences.set() that was already in
    // progress cannot finish after the next session removed the old cache.
    let cacheMutation: Promise<void> = Promise.resolve();

    function queueCacheMutation(operation: () => Promise<void>): Promise<void> {
        const queued = cacheMutation.catch(() => undefined).then(operation);
        cacheMutation = queued;
        return queued;
    }

    function currentSessionId(): string | null {
        const userId = SchultenMedia.User?.id;
        return userId === null || userId === undefined ? null : String(userId);
    }

    function applyFeatures(features: TenantFeatures): void {
        state.features = features;
        // Treat an absent flag as enabled (older backend without the key).
        state.trainingEnabled = features.training !== false;
        // Fail closed: only an explicit `true` enables the cycle screens.
        state.cycleEnabled = features.women_health === true;
        // Treat an absent flag as enabled (older backend without the key).
        state.creditsEnabled = features.credits !== false;
    }

    async function checkFeatures(force = false): Promise<TenantFeatures | null> {
        const generation = sessionGeneration;
        const sessionId = currentSessionId();
        const belongsToCurrentSession = () =>
            generation === sessionGeneration && sessionId === currentSessionId();

        if (!force && state.features !== null) return state.features;

        if (!force) {
            // Wait for a preceding session clear before reading Preferences.
            await cacheMutation.catch(() => undefined);
            if (!belongsToCurrentSession()) return state.features;

            const cached = await Preferences.get({ key: FEATURES_CACHE_KEY });
            if (!belongsToCurrentSession()) return state.features;

            if (cached.value) {
                const { features, timestamp, sessionId: cachedSessionId } = JSON.parse(cached.value);
                if (
                    sessionId !== null &&
                    cachedSessionId === sessionId &&
                    Date.now() - timestamp < CACHE_TTL_MS
                ) {
                    applyFeatures(features);
                    return state.features;
                }
            }
        }

        try {
            const response = await TenantService.getFeatures();
            if (!belongsToCurrentSession()) return state.features;

            if (response.success && response.data) {
                applyFeatures(response.data);
                if (sessionId !== null) {
                    await queueCacheMutation(async () => {
                        if (!belongsToCurrentSession()) return;

                        await Preferences.set({
                            key: FEATURES_CACHE_KEY,
                            value: JSON.stringify({
                                features: response.data,
                                timestamp: Date.now(),
                                sessionId,
                            }),
                        });
                    });
                }
            }
        } catch {
            // Keep the optimistic defaults on failure (training stays enabled).
        }

        return state.features;
    }

    /**
     * Forget everything we know about the current tenant. Both caches are tied
     * to a session: the in-memory one short-circuits `checkFeatures()` for the
     * lifetime of the process, and the persisted one answers for an hour
     * without touching the network. Without this, signing in as a coachee of
     * another tenant inherits the previous tenant's flags - and because
     * `cycleEnabled` is fail-closed, an inherited `women_health: false` hides
     * the cycle screens for someone who is entitled to them.
     */
    async function clearFeatures(): Promise<void> {
        sessionGeneration += 1;
        state.features = null;
        state.trainingEnabled = true;
        state.cycleEnabled = false;
        state.creditsEnabled = true;

        await queueCacheMutation(() => Preferences.remove({ key: FEATURES_CACHE_KEY }));
    }

    return {
        ...toRefs(state),
        checkFeatures,
        clearFeatures,
    };
});
